import React, { useMemo } from 'react';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { TableData } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CustomFieldRow,
	UpdateCustomDetail,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/types.ts';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { getCustomFieldsTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/CustomFieldsTable/getCustomFieldsTableColumns.tsx';

interface Props<TField extends CustomFieldRow & TableData> {
	fields: TField[];
	isLoading: boolean;
	hasPermission: boolean;
	onEdit: (field: TField) => void;
	onToggleChecked: (args: UpdateCustomDetail) => void;
}

export const CustomFieldsTable = <TField extends CustomFieldRow & TableData>({
	fields,
	isLoading,
	hasPermission = true,
	onEdit,
	onToggleChecked,
}: Props<TField>) => {
	const tMipManage = useTranslationMipManage();

	const columns = useMemo(
		() =>
			getCustomFieldsTableColumns({
				tMipManage,
				handlers: { onEdit, onToggleChecked },
				isLoading,
				hasPermission,
			}),
		[tMipManage, onEdit, onToggleChecked, isLoading, hasPermission]
	);

	return (
		<MantineTable<TField>
			data={fields}
			columns={columns}
			isLoading={isLoading}
			getRowId={(row) => row.id}
			enableTopToolbar={false}
			enableFilters={false}
			enableGlobalFilter={false}
			enableRowSelection={false}
			enableRowActions={false}
			enablePagination={false}
			manualPagination={false}
			renderEmptyRowsFallback={() => (
				<EmptyState
					description={tMipManage('investor_data.custom_fields.no_fields')}
					iconSize="sm"
					icon="es-box-magnifying-glass"
					style={{ paddingTop: '2rem', paddingBottom: '2rem' }}
				/>
			)}
		/>
	);
};
